import {
  PublicTransportConfiguration,
  HighwaysConfiguration,
  ParkingConfiguration,
} from '@shared/components';
import { CompanyPolicyResponse, ServiceType } from '@interfaces/models';
import { ZoneType, CityDto } from '@interfaces/types';
import { useHasPermission } from '@shared/hooks';
import { useCallback, useMemo } from 'react';
import { Loading } from '@ui/components';

import { useEmployeesProfileServicesData } from './use-employees-profile-services-data';

interface EmployeesProfileServicesProps {
  data?: CompanyPolicyResponse;
}

const nameCompare = <T extends Record<'name', string>>(a: T, b: T) =>
  a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

export const EmployeesProfileServices = ({ data }: EmployeesProfileServicesProps) => {
  const {
    saveCompanyPolicyConfiguration,
    isGeneralConfigLoading,
    lastServiceEnabled,
    companyPolicyData,
    availableServices,
    isQueryLoading,
  } = useEmployeesProfileServicesData(data);

  const hasEditPermission = useHasPermission('EMPLOYEES_PROFILE_MANAGE_SERVICES', 'WRITE');

  const cities = useMemo(
    () => companyPolicyData?.cities?.sort(nameCompare) ?? [],
    [companyPolicyData],
  );
  const zones = useMemo(
    () => companyPolicyData?.zones?.sort(nameCompare) ?? [],
    [companyPolicyData],
  );

  const getCitiesByZoneType = useCallback(
    (zoneType: ZoneType): CityDto[] => {
      return (
        cities
          .filter((city) => {
            const cityCode = city.code;
            return zones.some((zone) => zone.type === zoneType && zone.cityCode === cityCode);
          })
          .sort(nameCompare) ?? []
      );
    },
    [cities, zones],
  );

  const getZonesOfType = useCallback(
    (type: ZoneType) => zones.filter((zone) => zone.type === type),
    [zones],
  );

  if (isQueryLoading || isGeneralConfigLoading) {
    return <Loading />;
  }

  return (
    <Loading isLoading={!data}>
      <div className="space-y-6">
        {availableServices.includes(ServiceType.PARKINGS) && (
          <ParkingConfiguration
            onConfigurationChange={(data) => {
              saveCompanyPolicyConfiguration({
                parkingConfiguration: data,
              });
            }}
            toggleDisabled={lastServiceEnabled === ServiceType.PARKINGS || !hasEditPermission}
            initialConfiguration={data?.services.parkingConfiguration || null}
            className="bg-background outline-none shadow-xs"
            cities={getCitiesByZoneType(ZoneType.PARKING)}
            zones={getZonesOfType(ZoneType.PARKING)}
            days={companyPolicyData?.days ?? []}
            canNotEdit={!hasEditPermission}
          />
        )}
        {availableServices.includes(ServiceType.HIGHWAYS) && (
          <HighwaysConfiguration
            onConfigurationChange={(data) => {
              saveCompanyPolicyConfiguration({
                highwaysConfiguration: data,
              });
            }}
            toggleDisabled={lastServiceEnabled === ServiceType.HIGHWAYS || !hasEditPermission}
            initialConfiguration={data?.services.highwaysConfiguration || null}
            highwaySections={companyPolicyData?.highwaySections ?? []}
            className="bg-background outline-none shadow-xs"
            days={companyPolicyData?.days ?? []}
            canNotEdit={!hasEditPermission}
          />
        )}
        {availableServices.includes(ServiceType.PUBLIC_TRANSPORT) && (
          <PublicTransportConfiguration
            onConfigurationChange={(data) => {
              saveCompanyPolicyConfiguration({
                publicTransportConfiguration: data,
              });
            }}
            toggleDisabled={
              lastServiceEnabled === ServiceType.PUBLIC_TRANSPORT || !hasEditPermission
            }
            initialConfiguration={data?.services.publicTransportConfiguration || null}
            cities={getCitiesByZoneType(ZoneType.PUBLIC_TRANSPORT)}
            zones={getZonesOfType(ZoneType.PUBLIC_TRANSPORT)}
            className="bg-background outline-none shadow-xs"
            days={companyPolicyData?.days ?? []}
            canNotEdit={!hasEditPermission}
          />
        )}
      </div>
    </Loading>
  );
};
